import type { StoryblokPage } from "@made-people/nestjs-bff-service";
import {
  isStoryblokPreviewRequestQuery,
  StoryblokComponentList,
  StoryblokStoryProvider,
  useStoryblokStory,
} from "@made-people/grease-storyblok";
import { BffClient } from "@made-people/gears-bff-client";
import type { ISbStoryData } from "@storyblok/js";
import { useLoaderData, useLocation } from "react-router";
import {
  useGearsConfigurationVariable,
  useGearsServiceUrl,
} from "@made-people/gears-configuration";
import { gearsLoader } from "@made-people/gears-remix";

export type StoryblokRouteProps = {
  page: StoryblokPage;
};

/**
 * Renders a Storyblok story with all its children in a loop.
 */
export const StoryblokPageRoute = (props: StoryblokRouteProps) => {
  const {
    pageResponse: { frontendContext },
  } = useLoaderData<typeof gearsLoader>();

  const location = useLocation();
  const bffUrl = useGearsServiceUrl("bff");
  const tenantId = useGearsConfigurationVariable("tenantId");

  // Runs the story through the BFF to display content in the visual
  // editor the same way as it would be displayed on the normal frontend.
  function previewStoryResolver(story: ISbStoryData) {
    const bffClient = new BffClient({ baseUrl: bffUrl, tenantId });
    return bffClient.resolveStoryblokStory({
      story: story as StoryblokPage["storyblokData"],
      frontendContext,
    }) as Promise<ISbStoryData>;
  }

  return (
    <StoryblokStoryProvider
      story={props.page.storyblokData as ISbStoryData}
      insideStoryblokPreview={isStoryblokPreviewRequestQuery(
        new URLSearchParams(location.search),
      )}
      previewStoryResolver={previewStoryResolver}
    >
      <Components />
    </StoryblokStoryProvider>
  );
};

/**
 * Separate wrapper component, so we use the data hook instead of props
 * and visual preview works correctly.
 */
const Components = () => {
  const { story } = useStoryblokStory();
  if (!story?.content?.pageContent) {
    return null;
  }

  let seen = false;
  return (
    <StoryblokComponentList
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      components={story.content.pageContent.map((blok: any) => {
        if (blok.component !== "Banner" || seen) {
          return blok;
        }
        seen = true;
        return {
          ...blok,
          // Set first banner in content to main content
          isMainContent: true,
        };
      })}
    />
  );
};
